import type { ChatMessage } from '@/stores/useAiAssistantStore';

export type ThoughtStep = { key: string; icon: string; title: string; text: string };

/** 与 AiAssistantPanel 一致：解析后端 thought JSON */
export function parseThought(msg: ChatMessage): ThoughtStep[] {
  if (!msg.thought) return [];
  try {
    const parsed = JSON.parse(msg.thought) as Record<string, unknown>;
    const thoughtSteps = Array.isArray(parsed.steps) ? (parsed.steps as Array<Record<string, unknown>>) : [];
    if (thoughtSteps.length) {
      return thoughtSteps.map((step, idx) => ({
        key: String(step.key ?? idx),
        icon: String(step.icon ?? '•'),
        title: String(step.title ?? '步骤'),
        text: String(step.text ?? step.content ?? step.message ?? ''),
      }));
    }
    const steps: ThoughtStep[] = [];
    const retrieved = (parsed.retrieved_data as Record<string, unknown>) || {};
    const tools = Array.isArray(retrieved.requested_tools) ? (retrieved.requested_tools as string[]) : [];
    const toolResults = (retrieved.tool_results as Record<string, unknown>) || {};
    if (tools.length || Object.keys(toolResults).length) {
      const lines: string[] = [];
      if (tools.length) lines.push(`已调用工具：${tools.join('、')}`);
      for (const name of Object.keys(toolResults)) {
        const value = toolResults[name];
        lines.push(`${name}：${typeof value === 'string' ? value : JSON.stringify(value, null, 2)}`);
      }
      steps.push({ key: 'retrieval', icon: '🔍', title: '检索数据', text: lines.join('\n\n') });
    }
    if (parsed.analysis_result != null && String(parsed.analysis_result).trim()) {
      steps.push({ key: 'analysis', icon: '🧠', title: '深度分析', text: String(parsed.analysis_result) });
    }
    if (parsed.solution_plan != null && String(parsed.solution_plan).trim()) {
      steps.push({ key: 'solution', icon: '💡', title: '解决方案', text: String(parsed.solution_plan) });
    }
    if (parsed.final_answer != null && String(parsed.final_answer).trim()) {
      steps.push({ key: 'suggestion', icon: '📋', title: '具体建议', text: String(parsed.final_answer) });
    }
    return steps;
  } catch {
    return [];
  }
}

export function getMessageThoughtSteps(msg: ChatMessage): ThoughtStep[] {
  return parseThought(msg);
}

/** 流式 SSE 单步事件（/api/ai/chat/stream） */
export interface AiChatStreamEvent {
  event?: string;
  step?: string;
  title?: string;
  status?: string;
  data?: unknown;
  content?: string;
  answer?: string;
  thought?: unknown;
  structuredResult?: Record<string, unknown>;
  nextQuestions?: string[];
  conversationId?: string;
}

function formatStreamStepText(data: unknown, status?: string): string {
  if (data == null) {
    return status === 'running' ? '进行中…' : '';
  }
  if (typeof data === 'string') return data.trim();
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const message = String(o.message ?? o.summary ?? o.intent ?? o.content ?? '').trim();
    if (message) return message;
    try {
      return JSON.stringify(o, null, 2);
    } catch {
      return String(data);
    }
  }
  return String(data);
}

/** 将流式 step 事件转为可展示的思考步骤 */
export function streamEventToThoughtStep(event: AiChatStreamEvent): ThoughtStep | null {
  const stepKey = String(event.step ?? event.event ?? '').trim();
  if (!stepKey || stepKey === 'final') return null;
  const title = String(event.title ?? stepKey).trim() || '处理中';
  const status = String(event.status ?? '').trim();
  const text = formatStreamStepText(event.data, status) || (status === 'running' ? '进行中…' : '已完成');
  const icon = status === 'running' ? '⏳' : status === 'done' ? '✓' : '•';
  const displayTitle = status === 'running' ? `${title}（进行中）` : title;
  return {
    key: `${stepKey}::${status || 'info'}`,
    icon,
    title: displayTitle,
    text,
  };
}

export function thoughtStepsToJson(steps: ThoughtStep[]): string {
  return JSON.stringify({ steps }, null, 0);
}

/** 同一步骤 running/done 时以后者覆盖前者 */
export function mergeStreamingThoughtSteps(existing: ThoughtStep[], incoming: ThoughtStep): ThoughtStep[] {
  const baseKey = incoming.key.split('::')[0] || incoming.key;
  const next = existing.filter((s) => (s.key.split('::')[0] || s.key) !== baseKey);
  next.push(incoming);
  return next;
}

/** 解析 final 事件中的 thought，并合并流式步骤 */
export function buildFinalThoughtJson(
  streamSteps: ThoughtStep[],
  thoughtRaw: unknown,
): string {
  const fromStream = streamSteps.length ? streamSteps : [];
  if (thoughtRaw == null) {
    return thoughtStepsToJson(fromStream);
  }
  try {
    const parsed =
      typeof thoughtRaw === 'string'
        ? (JSON.parse(thoughtRaw) as Record<string, unknown>)
        : (thoughtRaw as Record<string, unknown>);
    if (!parsed || typeof parsed !== 'object') {
      return thoughtStepsToJson(fromStream);
    }
    const existingSteps = Array.isArray(parsed.steps)
      ? (parsed.steps as Array<Record<string, unknown>>).map((step, idx) => ({
          key: String(step.key ?? idx),
          icon: String(step.icon ?? '•'),
          title: String(step.title ?? '步骤'),
          text: String(step.text ?? step.content ?? step.message ?? ''),
        }))
      : [];
    const merged = [...fromStream];
    for (const s of existingSteps) {
      const base = s.key.split('::')[0] || s.key;
      if (!merged.some((m) => (m.key.split('::')[0] || m.key) === base)) {
        merged.push(s);
      }
    }
    return JSON.stringify({ ...parsed, steps: merged }, null, 0);
  } catch {
    return thoughtStepsToJson(fromStream);
  }
}

/** 从 final 事件解析与 sync 接口一致的结构化结果 */
export function extractStructuredFromFinalEvent(event: AiChatStreamEvent): {
  structuredResult?: Record<string, unknown>;
  nextQuestions?: string[];
} {
  let structuredResult = event.structuredResult;
  let nextQuestions = event.nextQuestions;
  const thought = event.thought;
  if (structuredResult == null && thought != null) {
    try {
      const t = typeof thought === 'string' ? (JSON.parse(thought) as Record<string, unknown>) : (thought as Record<string, unknown>);
      const direct = t.structured_result ?? t.structuredResult;
      if (direct && typeof direct === 'object') {
        structuredResult = direct as Record<string, unknown>;
      }
      const cp = t.create_project;
      if (!structuredResult && cp && typeof cp === 'object') {
        const sr = (cp as Record<string, unknown>).structured_result ?? (cp as Record<string, unknown>).structuredResult;
        if (sr && typeof sr === 'object') structuredResult = sr as Record<string, unknown>;
      }
      const nq = t.nextQuestions ?? t.next_questions;
      if (Array.isArray(nq)) nextQuestions = nq.map((v) => String(v)).filter(Boolean);
    } catch {
      /* ignore */
    }
  }
  return { structuredResult, nextQuestions };
}

/**
 * live（进行中）思考区的展示状态。
 * 由各组件传入自己的 live 区显示条件，必须与模板中 live 块的 v-if 逐字一致，
 * 否则会出现「两个框都显示」或「两个框都不显示」。
 */
export type LiveThoughtView = {
  /** live 区当前是否可见 */
  visible: boolean;
  /** live 区当前展示的步骤数 */
  stepCount: number;
  /** live 区当前归属的消息 id（正在生成的那条 assistant 消息） */
  ownerMessageId: string;
};

/**
 * 消息内 inline 思考框是否展示。
 * 与 live 区互斥：流式进行中这条消息的步骤正由 live 区展示，inline 框需让位，
 * 否则同一批步骤会在同一屏渲染两遍（两个思考框）。
 */
export function shouldShowInlineThought(msg: ChatMessage, live: LiveThoughtView): boolean {
  if (msg.role !== 'assistant') return false;
  if (!getMessageThoughtSteps(msg).length) return false;
  if (live.visible && live.stepCount > 0 && msg.id === live.ownerMessageId) return false;
  return true;
}
